import React, { useEffect, useState } from 'react';
import type { MultiValue } from 'react-select';

import { usePokCore } from '../../../common/hooks/usePokCore';
import { GetOneToolTeamDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';

import { Option, Selector } from './Selector';

interface OneToolTeamSelectorProps {
  value?: string | null;
  readOnly?: boolean;
  onChange?: (option: Option | MultiValue<Option> | null | undefined) => void;
}

const toOption = (team: GetOneToolTeamDto): Option => ({
  value: team.id,
  label: `[${team.oneToolId}] ${team.name}`,
});

export const OneToolTeamSelector: React.FC<
  OneToolTeamSelectorProps
> = props => {
  const pok = usePokCore();
  const notifications = useNotifications();
  const [teams, setTeams] = useState<GetOneToolTeamDto[]>([]);

  useEffect(() => {
    let mounted = true;
    pok.oneToolTeams
      .findAllActive()
      .then(data => {
        if (mounted) {
          setTeams(data);
        }
      })
      .catch(e => notifications.caughtError(e));
    return () => {
      mounted = false;
    };
  }, [pok.oneToolTeams, notifications]);

  const provider = async (text: string) => {
    const lower = text.toLocaleLowerCase();
    return teams
      .filter(
        t =>
          t.name.toLocaleLowerCase().includes(lower) ||
          String(t.oneToolId).includes(lower),
      )
      .sort((a, b) => a.name.localeCompare(b.name, 'pl'))
      .map(toOption);
  };

  return (
    <Selector
      uniqueKey={'one-tool-team-' + JSON.stringify(teams)}
      readOnly={props.readOnly}
      creatable={false}
      clearable={true}
      provider={provider}
      value={props.value ?? undefined}
      deletedValueName={id => pok.oneToolTeams.getById(id).then(toOption)}
      onChange={props.onChange}
    />
  );
};
